/**
 * Stripe Service
 * 
 * Handles Stripe integration for payment processing and checkout sessions.
 */

import { loadStripe } from '@stripe/stripe-js';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    if (!STRIPE_PUBLISHABLE_KEY) {
      console.error('❌ REACT_APP_STRIPE_PUBLISHABLE_KEY not configured');
      return null;
    }
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

export class StripeService {
  /**
   * Create checkout session and redirect to Stripe Checkout
   */
  static async createCheckoutSession(planType, token) {
    console.log('🛒 StripeService.createCheckoutSession called with:', {
      planType,
      tokenExists: !!token,
      tokenLength: token ? token.length : 0,
      apiBase: API_BASE
    });
    
    try {
      const response = await fetch(`${API_BASE}/api/v1/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_type: planType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const { checkout_url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
      
      return { success: true };
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw new Error(`Betalning misslyckades: ${error.message}`);
    }
  }

  /**
   * Get user's payment history
   */
  static async getPaymentHistory(token) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/stripe/payment-history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
      
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  /**
   * Get customer portal URL for subscription management
   */
  static async getCustomerPortal(token) {
    try {
      const response = await fetch(`${API_BASE}/api/v1/stripe/customer-portal`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const { portal_url } = await response.json();
      
      // Redirect to Stripe Customer Portal
      window.location.href = portal_url;
      
      return { success: true };
      
    } catch (error) {
      console.error('Error getting customer portal:', error);
      throw error;
    }
  }

  /**
   * Check Stripe health status
   */
  static async checkStripeHealth() {
    try {
      const response = await fetch(`${API_BASE}/api/v1/stripe/health`);
      return await response.json();
    } catch (error) {
      console.error('Stripe health check failed:', error);
      return { status: 'unhealthy', error: error.message };
    }
  }

  /**
   * Get available plans from backend
   */
  static async getAvailablePlans() {
    try {
      const response = await fetch(`${API_BASE}/api/v1/stripe/plans`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Return fallback plans
      return {
        plans: [
          {
            id: 'prova',
            name: 'Prova Plan',
            price_sek: 49,
            credits: 50,
            popular: false
          },
          {
            id: 'start',
            name: 'Start Plan',
            price_sek: 99,
            credits: 150,
            popular: true
          },
          {
            id: 'power',
            name: 'Power Plan',
            price_sek: 349,
            credits: 1000,
            popular: false
          }
        ]
      };
    }
  }
}

export default StripeService;
